import {
  FormSelectProps,
  SELECT_STYLES,
  SelectOption,
} from '@c/forms/controls/FormSelect';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import { cx } from 'class-variance-authority';
import { Fragment } from 'react';

export default function Select({
  allowEmpty = true,
  onChange,
  value: idValue,
  options,
  placeholder = '',
  disabled,
  multiple,
  error,
  className,
  customPlaceholder,
  rounded,
  grow = true,
}: Omit<FormSelectProps, 'control' | 'name'> & {
  value?: string | string[];
  className?: string;
  rounded?: boolean;
  grow?: boolean;
}) {
  const value = multiple
    ? options.filter((o) =>
        Array.isArray(idValue)
          ? idValue.includes(o.id)
          : idValue?.split(',').includes(o.id)
      )
    : options.find((o) => o.id === idValue) ?? '';
  if (allowEmpty && !!value && !options?.some((o) => o.id === 'empty')) {
    options.unshift({ id: 'empty', label: '', value: '' });
  }

  return (
    <Listbox
      value={value}
      onChange={onChange}
      disabled={disabled}
      multiple={multiple}
    >
      {({ open }) => (
        <div className="relative w-full">
          <ListboxButton
            className={`${SELECT_STYLES({
              error,
              disabled,
              rounded,
            })} ${className}`}
          >
            <span
              className={
                'block min-h-[2.25rem] min-w-[2.4rem] truncate pr-10 text-[1.6rem] font-medium text-black'
              }
            >
              {multiple
                ? value
                  ? (value as SelectOption<string>[])
                      .map((v) => v.JSXLabel || v.label)
                      .join(', ')
                  : customPlaceholder || placeholder
                : (value as SelectOption<string>)?.JSXLabel ||
                  (value as SelectOption<string>)?.label ||
                  customPlaceholder ||
                  placeholder}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-[2rem] flex items-center pr-2">
              <ChevronDownIcon
                className="h-8 w-8 text-zinc-600"
                aria-hidden="true"
              />
            </span>
          </ListboxButton>

          <Transition
            show={open}
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <ListboxOptions
              className={cx(
                'absolute left-0 z-30 mt-2 max-h-96 overflow-auto rounded-[1rem] bg-brand-white py-1 text-[1.8rem] focus:outline-none sm:text-[1.6rem]',
                grow ? 'w-auto min-w-[100%] whitespace-nowrap' : 'w-full'
              )}
            >
              {options.map((option) => (
                <ListboxOption
                  key={option.id}
                  className={({ active }) =>
                    cx(
                      active ? 'bg-brand-secondary-light' : '',
                      'relative flex h-16 select-none items-center pl-10 pr-9 sm:h-12'
                    )
                  }
                  value={option}
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={cx(
                          'block',
                          grow ? 'whitespace-nowrap' : 'truncate'
                        )}
                      >
                        {option.JSXLabel || option.label}
                      </span>

                      {selected ? (
                        <span
                          className={cx(
                            'absolute inset-y-0 right-0 flex items-center pr-4'
                          )}
                        >
                          <CheckIcon
                            className="h-10 w-10 sm:h-5 sm:w-5"
                            aria-hidden="true"
                          />
                        </span>
                      ) : null}
                    </>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Transition>
        </div>
      )}
    </Listbox>
  );
}
